import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { PersonOutlined } from '@mui/icons-material';
import Skeleton from '@mui/material/Skeleton';
import { ThemeProvider } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { HiPencil } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { ModalNotification } from 'components/common/Modal';
import Result from 'components/v2/common/Result';
import * as S from './ClientProfile.styled';
import ActivateAccountButton from './components/ActivateAccountButton';
import AdditionalDetails from './components/AdditionalDetails';
import BusinessOwnership from './components/BusinessOwnership';
import Education from './components/Education';
import FamilyMembers from './components/FamilyMembers';
import GeneralInfo from './components/GeneralInfo';
import JobOffer from './components/JobOffer';
import LanguageProficiency from './components/LanguageProficiency';
import ProfileSectionNav from './components/ProfileSectionNav';
import RelativesAbroad from './components/RelativesAbroad';
import SpousePartner from './components/SpousePartner';
import WorkExperience from './components/WorkExperience';
import { FormsFactory } from 'utils/formsFactory';

import api, { CandidateInfo as CandidateUserData } from 'api';
import { CandidatePersonalInfoData } from 'api/models/response/TableInput';
import type {
    SpouseEducationProfile,
    SpouseLanguageProfile,
    SpouseProfile,
    SpouseWorkExperienceProfile,
} from 'api/models/response/SpouseProfile';
import { useAuth } from 'contexts/ProtectedRoute';
import { VisaPurposeServerValues } from 'data/constants/constants';
import { CANDIDATE_ROLES } from 'data/constants/roles';
import { useCompletedForms } from 'hooks/useCompletedForms';
import {
    mergeSpouseProfiles,
    normalizeSpouseProfile,
    useSpouseProfile,
} from 'hooks/useSpouseProfile';
import { useWindowSize } from 'hooks/useWindowSize';
import { formCounterSlice } from 'redux/formCounter';
import { RootState } from 'redux/store';
import { createDomainTheme } from 'theme/domainTheme';
import { requiredProfileFormKeys } from 'utils/completedAssessment';
import { getDomainBranding } from 'utils/domainBranding';
import { getDomainPaletteCssVars } from 'utils/domainPalette';
import {
    parseHasSpouseFromPayload,
    parseSpouseUserIdFromPayload,
    persistSpouseLifecycle,
} from 'utils/hasSpouse';
import { ProfileEditContext } from './components/ProfileEditContext';
import { RELATED_FROM_QUERY_KEY } from './components/relatedProfile';
import type { ProfileSectionGroupId, ProfileSectionMeta } from './profileSections';

export type Spouse = SpouseProfile;

export type ProfileUserData = CandidateUserData & {
    language_setting?: string | null;
};

export type Relative = {
    accompanying?: boolean | null;
    birth_date: Date | null;
    country_of_birth?: string | null;
    country_of_residence: string | null;
    firstname: string | null;
    gender: string | null;
    lastname: string | null;
    middlename: string | null;
    nationality: string | null;
    relation: string | null;
    user_id?: number | null;
};

export type RelativeAbroad = {
    age: string | null;
    country: string | null;
    country_of_birth?: string | null;
    marital_status?: string | null;
    nationality?: string | null;
    region: string | null;
    relation: string | null;
    residence_since?: string | null;
    residency_status: string | null;
    user_id?: number | null;
};

export interface LanguageType extends SpouseLanguageProfile {
    spouse_id?: number | null;
    user_id?: number | null;
}

export interface EducationData extends SpouseEducationProfile {
    zip?: string | null;
    field_of_study_au_en?: string | null;
    field_of_study_au_id?: number | string | null;
    field_of_study_ca_en?: string | null;
    field_of_study_ca_id?: number | string | null;
    field_of_study_uk_en?: string | null;
    field_of_study_uk_id?: number | string | null;
    id?: number | null;
    user_id?: number | null;
}

export interface WorkExperienceData extends SpouseWorkExperienceProfile {
    anzsco?: number | null;
    id?: number | null;
    noc?: string | null;
    occupation_au_en?: string | null;
    occupation_au_id?: number | string | null;
    occupation_ca_en?: string | null;
    occupation_ca_id?: number | string | null;
    occupation_uk_en?: string | null;
    occupation_uk_id?: number | string | null;
    self_employed?: boolean | null;
    soc?: number | null;
    spouse_id?: number | null;
    user_id?: number | null;
    zip?: string | null;
}

export type Business = {
    annual_sales: number | null;
    city: string | null;
    country: string | null;
    currency?: string | null;
    current: boolean;
    end: Date | null;
    net_company_value: number | null;
    net_profit: number | null;
    number_employees: number | null;
    percent_ownership: number | null;
    region: string | null;
    start: Date | null;
    type: string | null;
    user_id?: number | null;
};

export type JobOfferData = {
    zip?: string | null;
    anzsco?: number | null;
    country: string | null;
    hourly_wage: number | null;
    noc?: string | null;
    nomination: string | null;
    profession: string | null;
    region: string | null;
    soc?: number | null;
    user_id?: number | null;
    work_permit: string | null;
};

export interface Purpose {
    purpose: string;
}
export type CitizenshipData = {
    country: string | null;
    user_id: number | null;
};

export interface PrevVisaData {
    name: string | null;
    start: string | null;
    end: string | null;
    country: string | null;
}

const DEFAULT_SECTION_KEY = 'personal_info';

const ClientProfile = () => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const isRelatedProfile = Boolean(searchParams.get(RELATED_FROM_QUERY_KEY));
    const candidateID = Number(params.id);
    const spouseCandidateId =
        Number.isFinite(candidateID) && candidateID > 0 ? candidateID : undefined;
    const { role } = useAuth();
    const canEdit = !!role && !CANDIDATE_ROLES.includes(role);
    const [editMode, setEditMode] = useState<boolean>(false);
    const [selectedSectionKey, setSelectedSectionKey] = useState<string>(DEFAULT_SECTION_KEY);
    const [personalInfoData, setPersonalInfoData] = useState<null | CandidatePersonalInfoData>(
        null
    );
    const [prevVisaData, setPrevVisaData] = useState<null | PrevVisaData[]>(null);
    const [citizenshipData, setCitizenshipData] = useState<null | CitizenshipData[]>(null);
    const [userData, setUserData] = useState<null | ProfileUserData>(null);
    const [spouseUserKind, setSpouseUserKind] = useState<string | null>(null);
    const [promotionNotification, setPromotionNotification] = useState<string | null>(null);
    const [assessmentDateNotification, setAssessmentDateNotification] = useState<{
        variant: 'success' | 'error';
        message: string;
    } | null>(null);
    const [isUpdatingAssessmentDate, setIsUpdatingAssessmentDate] = useState(false);
    const [purpose, setPurpose] = useState<null | Purpose>(null);
    const [hasSpouseFlag, setHasSpouseFlag] = useState<boolean | undefined>(undefined);
    const [candidateProfileSpouse, setCandidateProfileSpouse] = useState<Spouse | null>(null);
    const [familyData, setFamilyData] = useState<null | Relative[]>(null);
    const [languageData, setLanguageData] = useState<null | LanguageType[]>(null);
    const [educationData, setEducationData] = useState<null | EducationData[]>(null);
    const [workExperienceData, setWorkExperienceData] = useState<null | WorkExperienceData[]>(null);
    const [businessData, setBusinessData] = useState<null | Business[]>(null);
    const [relativesAbroadData, setRelativesAbroadData] = useState<null | RelativeAbroad[]>(null);
    const [jobOfferData, setJobOfferData] = useState<null | JobOfferData[]>(null);
    const hasCandidateProfileData = !!(
        userData ||
        personalInfoData ||
        citizenshipData ||
        prevVisaData
    );
    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    const spouseProfile = useSpouseProfile(spouseCandidateId, {
        enabled: spouseCandidateId !== undefined && !isRelatedProfile,
    });
    const spouseData = mergeSpouseProfiles(spouseProfile.spouse, candidateProfileSpouse);
    const hasSpouse =
        !isRelatedProfile &&
        (hasSpouseFlag === true || (hasSpouseFlag !== false && spouseData !== null));
    const requiredFormKeys = useMemo(
        () => requiredProfileFormKeys(isRelatedProfile ? false : hasSpouse),
        [hasSpouse, isRelatedProfile]
    );
    const { completedForms, refresh: refreshCompletedForms } = useCompletedForms(
        candidateID,
        requiredFormKeys
    );
    const dispatch = useDispatch();
    const showSpouse = useSelector((state: RootState) => state.formCounter.showSpouse);

    const branding = useMemo(() => getDomainBranding(), []);
    const domainTheme = useMemo(() => createDomainTheme(branding.palette), [branding]);
    const paletteCssVars = getDomainPaletteCssVars(branding.palette);

    const sectionsMeta: ProfileSectionMeta[] = useMemo(() => {
        const section = (
            key: string,
            labelKey: string,
            form: string,
            group: ProfileSectionGroupId,
            extras?: Partial<ProfileSectionMeta>
        ): ProfileSectionMeta => ({
            key,
            label: t(labelKey, { lng: language }),
            form,
            group,
            ...extras,
        });

        return [
            section('personal_info', 'personalInfo', 'Personal Info', 'identity'),
            section('additional_details', 'additionalDetails', 'Additional Details', 'identity'),
            ...(!isRelatedProfile
                ? [
                      section(
                          'spouse',
                          'spouse/CommonLawPartner',
                          'Spouse/Common Law Partner',
                          'family',
                          {
                              disabledWhenEditing: !hasSpouse,
                              disabledTooltip: t('noSpouseOnRecord', {
                                  lng: language,
                                  defaultValue:
                                      'No spouse on record. Add a partner in Additional Details first.',
                              }),
                          }
                      ),
                  ]
                : []),
            section('family_member', 'familyMembers', 'Family Members', 'family'),
            section('relative_abroad', 'relativesAbroad', 'Relatives Abroad', 'family'),
            section('language', 'languageProficiency', 'Language Proficiency', 'qualifications'),
            section('education', 'education', 'Education', 'qualifications'),
            section('work_experience', 'workExperience', 'Work Experience', 'qualifications'),
            section('business', 'businessOwnership', 'Business Ownership', 'visaPath'),
            section('job_offer', 'jobOffer', 'Job Offer', 'visaPath'),
        ];
    }, [language, t, hasSpouse, isRelatedProfile]);

    const metaByKey = useMemo(
        () => Object.fromEntries(sectionsMeta.map((section) => [section.key, section])),
        [sectionsMeta]
    );

    const activeMeta = metaByKey[selectedSectionKey] ?? metaByKey[DEFAULT_SECTION_KEY];
    const canRenderProfile = hasCandidateProfileData;

    const completedCount = useMemo(
        () => sectionsMeta.filter((section) => completedForms[section.key]).length,
        [sectionsMeta, completedForms]
    );

    useEffect(() => {
        if (!purpose?.purpose) {
            dispatch(formCounterSlice.actions.setPurpose(''));
            return;
        }
        const serverValue = purpose.purpose as unknown as VisaPurposeServerValues;
        const idx = Object.values(VisaPurposeServerValues).indexOf(serverValue);
        if (idx >= 0) {
            const key = Object.keys(VisaPurposeServerValues)[idx];
            dispatch(formCounterSlice.actions.setPurpose(key));
        }
    }, [purpose, dispatch]);

    const handleEnterEdit = () => {
        if (showSpouse) {
            dispatch(formCounterSlice.actions.setShowSpouse());
        }
        setEditMode(true);
    };

    const handleExitEdit = () => {
        if (showSpouse) {
            dispatch(formCounterSlice.actions.setShowSpouse());
        }
        setEditMode(false);
    };

    const handleSectionSaved = async () => {
        await Promise.all([
            refreshCompletedForms(),
            fetchData(candidateID),
            spouseProfile.refresh(),
        ]);
    };

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    const fetchData = async (id: number) => {
        try {
            const response = await api.candidateProfile.get(id);
            setPersonalInfoData(response.data.PersonalInfo);
            setCitizenshipData(response.data.Citizenship);
            setPrevVisaData(response.data.PreviousVisa);
            setUserData(response.data.User);
            setSpouseUserKind(response.data.Spouse?.user_kind ?? null);
            setCandidateProfileSpouse(normalizeSpouseProfile(response.data.Spouse, id));
            setPurpose(response.data.Purpose);
            setFamilyData(response.data.FamilyMember);
            setLanguageData(response.data.Language);
            setEducationData(response.data.Education);
            setWorkExperienceData(response.data.WorkExperience);
            setBusinessData(response.data.Business);
            setRelativesAbroadData(response.data.RelativeAbroad);
            setJobOfferData(response.data.JobOffer);
            await api.tableInput.fetchCandidateInputSales(
                (data: unknown) => {
                    const flag = parseHasSpouseFromPayload(data);
                    const spouseUserId = parseSpouseUserIdFromPayload(data);
                    if (flag !== undefined) {
                        setHasSpouseFlag(flag);
                    }
                    if (!isRelatedProfile && (flag !== undefined || spouseUserId)) {
                        persistSpouseLifecycle(flag, spouseUserId);
                    }
                },
                'AdditionalDetails',
                id
            );
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (candidateID) {
            setHasSpouseFlag(undefined);
            setCandidateProfileSpouse(null);
            fetchData(candidateID);
            setPromotionNotification(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidateID]);

    const handleAccountPromoted = async () => {
        setPromotionNotification(
            t('accountPromotedSuccessfully', {
                lng: language,
                defaultValue: 'Account successfully promoted.',
            })
        );
        await fetchData(candidateID);
    };

    const handleUpdateAssessmentDate = async () => {
        if (!Number.isFinite(candidateID) || candidateID <= 0) {
            return;
        }

        setAssessmentDateNotification(null);
        setIsUpdatingAssessmentDate(true);
        try {
            const response = await api.tableInput.updateAssessmentCompletionDate(candidateID);
            setUserData((currentUserData) =>
                currentUserData?.id === candidateID
                    ? {
                          ...currentUserData,
                          completed_assessment: response.data.completed_assessment,
                      }
                    : currentUserData
            );
            setAssessmentDateNotification({
                variant: 'success',
                message: t('assessmentCompletionDateUpdated', { lng: language }),
            });
        } catch (error) {
            console.error('Failed to update assessment completion date', error);
            setAssessmentDateNotification({
                variant: 'error',
                message: t('assessmentCompletionDateUpdateFailed', { lng: language }),
            });
        } finally {
            setIsUpdatingAssessmentDate(false);
        }
    };

    useEffect(() => {
        setSelectedSectionKey(DEFAULT_SECTION_KEY);
        setEditMode(false);
        setAssessmentDateNotification(null);
    }, [candidateID]);

    useEffect(() => {
        const meta = metaByKey[selectedSectionKey];
        if (editMode && meta?.disabledWhenEditing) {
            setSelectedSectionKey(DEFAULT_SECTION_KEY);
        }
    }, [editMode, selectedSectionKey, metaByKey]);

    const profileLoadingContent = () => (
        <>
            {[1, 2, 3, 4, 5].map((item) => (
                <S.InfoItem key={item}>
                    <Skeleton width={180} />
                    <Skeleton width={240} />
                </S.InfoItem>
            ))}
        </>
    );

    const spouseProfileErrorContent = () => (
        <Result
            icon={<PersonOutlined sx={{ fontSize: 32 }} />}
            title={t('unableToLoadProfile', {
                lng: language,
                defaultValue: 'Unable to load profile',
            })}
            description={t('unableToLoadSpouseProfile', {
                lng: language,
                defaultValue:
                    'The spouse or common-law partner profile could not be loaded. Please try again later.',
            })}
        />
    );

    const readOnlySectionContent = () => (
        <>
            {selectedSectionKey === 'personal_info' && (
                <GeneralInfo
                    mobile={mobile}
                    userData={userData}
                    personalInfoData={personalInfoData}
                    citizenshipData={citizenshipData}
                    purpose={purpose}
                />
            )}
            {selectedSectionKey === 'additional_details' && (
                <AdditionalDetails
                    citizenshipData={citizenshipData}
                    mobile={mobile}
                    personalInfoData={personalInfoData}
                    prevVisaData={prevVisaData}
                    spouse={hasSpouse}
                    fam={familyData !== null && familyData?.length > 0}
                />
            )}
            {selectedSectionKey === 'spouse' && (
                <>
                    {!hasSpouse ? (
                        <Result
                            icon={<PersonOutlined sx={{ fontSize: 32 }} />}
                            title={t('spouse/CommonLawPartner', { lng: language })}
                            description={t('noSpouseOnRecord', {
                                lng: language,
                                defaultValue:
                                    'No spouse on record. Add a partner in Additional Details first.',
                            })}
                        />
                    ) : spouseProfile.isBaseLoading && !spouseData ? (
                        profileLoadingContent()
                    ) : spouseProfile.isBaseError && !spouseData ? (
                        spouseProfileErrorContent()
                    ) : (
                        <>
                            {canEdit &&
                                spouseUserKind === 'PROFILE_ONLY' &&
                                !!spouseData?.user_id && (
                                    <div style={{ marginBottom: '14px' }}>
                                        <ActivateAccountButton
                                            userId={spouseData.user_id}
                                            onPromoted={handleAccountPromoted}
                                        />
                                    </div>
                                )}
                            <SpousePartner mobile={mobile} spouseData={spouseData} />
                        </>
                    )}
                </>
            )}
            {selectedSectionKey === 'family_member' && (
                <FamilyMembers mobile={mobile} familyData={familyData} />
            )}
            {selectedSectionKey === 'language' && (
                <LanguageProficiency
                    mobile={mobile}
                    languageData={languageData}
                    spouseLanguageData={null}
                />
            )}
            {selectedSectionKey === 'education' && (
                <Education
                    mobile={mobile}
                    educationData={educationData}
                    spouseEducationData={null}
                />
            )}
            {selectedSectionKey === 'work_experience' && (
                <WorkExperience
                    mobile={mobile}
                    workExperienceData={workExperienceData}
                    spouseWorkExperienceData={null}
                />
            )}
            {selectedSectionKey === 'relative_abroad' && (
                <RelativesAbroad mobile={mobile} relativesAbroadData={relativesAbroadData} />
            )}
            {selectedSectionKey === 'business' && (
                <BusinessOwnership mobile={mobile} businessData={businessData} />
            )}
            {selectedSectionKey === 'job_offer' && (
                <JobOffer mobile={mobile} jobOfferData={jobOfferData} />
            )}
        </>
    );

    const editSectionContent = () => {
        const englishLabel = activeMeta?.form ?? 'Personal Info';
        const formKey = activeMeta?.key ?? DEFAULT_SECTION_KEY;
        return (
            <S.EditPane>
                <ProfileEditContext.Provider
                    value={{
                        active: true,
                        formKey,
                        completedAssessment: userData?.completed_assessment ?? null,
                        requiredFormKeys,
                        isEmailOptional: isRelatedProfile && userData?.user_kind === 'PROFILE_ONLY',
                        saveLabel: t('saveSection', {
                            lng: language,
                            defaultValue: 'Save section',
                        }),
                        cancelLabel: t('cancel', { lng: language, defaultValue: 'Cancel' }),
                        onSaved: handleSectionSaved,
                        onCancel: handleExitEdit,
                    }}
                >
                    <div key={`${candidateID}-${englishLabel}-${showSpouse ? 'partner' : 'me'}`}>
                        {FormsFactory(englishLabel, showSpouse)}
                    </div>
                </ProfileEditContext.Provider>
            </S.EditPane>
        );
    };

    return (
        <ThemeProvider theme={domainTheme}>
            <S.OverviewContainer style={paletteCssVars as CSSProperties}>
                <ProfileSectionNav
                    sections={sectionsMeta}
                    selectedKey={selectedSectionKey}
                    completedByKey={completedForms}
                    editMode={editMode}
                    completedCount={completedCount}
                    canUpdateAssessmentDate={canEdit && userData !== null}
                    isUpdatingAssessmentDate={isUpdatingAssessmentDate}
                    onSelect={setSelectedSectionKey}
                    onUpdateAssessmentDate={handleUpdateAssessmentDate}
                />

                {canRenderProfile && (
                    <S.InfoSection mobile={mobile}>
                        <S.InfoSectionHeader>
                            <S.InfoSectionHeaderRow>
                                <S.InfoSectionHeaderText>
                                    <S.InfoSectionEyebrow>
                                        {editMode
                                            ? t('editingProfile', {
                                                  lng: language,
                                                  defaultValue: 'Editing profile',
                                              })
                                            : t('candidateProfile', {
                                                  lng: language,
                                                  defaultValue: 'Candidate profile',
                                              })}
                                    </S.InfoSectionEyebrow>
                                    <S.InfoSectionTitle>
                                        {activeMeta?.label ?? selectedSectionKey}
                                    </S.InfoSectionTitle>
                                    <S.InfoSectionSubtitle>
                                        {editMode
                                            ? t('editSectionSubtitle', {
                                                  lng: language,
                                                  defaultValue:
                                                      'Fill any section in any order. Each section saves independently.',
                                              })
                                            : t('candidateSectionSubtitle', {
                                                  lng: language,
                                                  defaultValue:
                                                      "Review the candidate's submitted information for this section.",
                                              })}
                                    </S.InfoSectionSubtitle>
                                </S.InfoSectionHeaderText>
                                <S.InfoSectionHeaderActions>
                                    {!editMode &&
                                        canEdit &&
                                        userData?.user_kind === 'PROFILE_ONLY' && (
                                            <ActivateAccountButton
                                                userId={candidateID}
                                                onPromoted={handleAccountPromoted}
                                            />
                                        )}
                                    {editMode ? (
                                        <S.EditProfileButton
                                            type="button"
                                            $quiet
                                            onClick={handleExitEdit}
                                        >
                                            {t('doneEditing', {
                                                lng: language,
                                                defaultValue: 'Done editing',
                                            })}
                                        </S.EditProfileButton>
                                    ) : (
                                        canEdit && (
                                            <S.EditProfileButton
                                                type="button"
                                                onClick={handleEnterEdit}
                                            >
                                                <HiPencil size={14} />
                                                {t('editProfile', {
                                                    lng: language,
                                                    defaultValue: 'Edit profile',
                                                })}
                                            </S.EditProfileButton>
                                        )
                                    )}
                                </S.InfoSectionHeaderActions>
                            </S.InfoSectionHeaderRow>
                        </S.InfoSectionHeader>
                        {promotionNotification && (
                            <div style={{ marginBottom: '12px' }}>
                                <ModalNotification
                                    variant="success"
                                    message={promotionNotification}
                                />
                            </div>
                        )}
                        {assessmentDateNotification && (
                            <div style={{ marginBottom: '12px' }}>
                                <ModalNotification
                                    variant={assessmentDateNotification.variant}
                                    message={assessmentDateNotification.message}
                                />
                            </div>
                        )}
                        <S.InfoSectionBody>
                            {editMode ? editSectionContent() : readOnlySectionContent()}
                        </S.InfoSectionBody>
                    </S.InfoSection>
                )}
                {!userData && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {[1, 2, 3, 4, 5, 6, 7].map((element, i) => (
                            <div key={i} style={{ display: 'flex', gap: '1rem', height: '2.8rem' }}>
                                <Skeleton height={75} width={200} />
                                <Skeleton height={75} width={200} />
                            </div>
                        ))}
                    </div>
                )}
            </S.OverviewContainer>
        </ThemeProvider>
    );
};

export default ClientProfile;
